(() => {
  const storageKey = 'cds-a11y-v2';
  const defaults = {
    text: 0,
    contrast: false,
    dark: false,
    links: false,
    dyslexia: false,
    motion: false,
  };

  const maxText = 4;
  const minText = -2;

  const readState = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return { ...defaults };
      return { ...defaults, ...JSON.parse(raw) };
    } catch (_e) {
      return { ...defaults };
    }
  };

  const writeState = (state) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (_e) {
      // no-op
    }
  };

  const applyState = (state) => {
    const root = document.documentElement;
    root.classList.toggle('a11y-contrast', Boolean(state.contrast));
    root.classList.toggle('a11y-dark', Boolean(state.dark));
    root.classList.toggle('a11y-links', Boolean(state.links));
    root.classList.toggle('a11y-dyslexia', Boolean(state.dyslexia));
    root.classList.toggle('a11y-motion-reduce', Boolean(state.motion));

    const percent = Math.max(88, Math.min(124, 100 + Number(state.text) * 6));
    root.style.fontSize = `${percent}%`;
  };

  const init = () => {
    const widget = document.querySelector('[data-a11y-widget]');
    if (!(widget instanceof HTMLElement)) return;

    const panel = widget.querySelector('[data-a11y-panel]');
    const trigger = widget.querySelector('[data-a11y-trigger]');
    const closeBtn = widget.querySelector('[data-a11y-close]');
    const resetBtn = widget.querySelector('[data-a11y-reset]');
    const decBtn = widget.querySelector('[data-a11y-text-dec]');
    const incBtn = widget.querySelector('[data-a11y-text-inc]');
    const output = widget.querySelector('[data-a11y-text-value]');
    const switches = [...widget.querySelectorAll('[data-a11y-toggle]')];

    if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) return;

    let state = readState();
    applyState(state);

    const syncUI = () => {
      if (output instanceof HTMLOutputElement) {
        output.value = String(state.text);
        output.textContent = String(state.text);
      }
      for (const sw of switches) {
        if (!(sw instanceof HTMLButtonElement)) continue;
        const key = sw.dataset.a11yToggle;
        if (!key || !(key in state)) continue;
        sw.setAttribute('aria-checked', state[key] ? 'true' : 'false');
      }
    };

    const open = () => {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    };

    trigger.addEventListener('click', () => {
      if (panel.hidden) open();
      else close();
    });

    closeBtn?.addEventListener('click', close);

    decBtn?.addEventListener('click', () => {
      state.text = Math.max(minText, Number(state.text) - 1);
      applyState(state);
      writeState(state);
      syncUI();
    });

    incBtn?.addEventListener('click', () => {
      state.text = Math.min(maxText, Number(state.text) + 1);
      applyState(state);
      writeState(state);
      syncUI();
    });

    for (const sw of switches) {
      if (!(sw instanceof HTMLButtonElement)) continue;
      sw.addEventListener('click', () => {
        const key = sw.dataset.a11yToggle;
        if (!key || !(key in state)) return;
        state[key] = !Boolean(state[key]);
        applyState(state);
        writeState(state);
        syncUI();
      });
    }

    resetBtn?.addEventListener('click', () => {
      state = { ...defaults };
      applyState(state);
      writeState(state);
      syncUI();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panel.hidden) return;
      if (widget.contains(target)) return;
      close();
    });

    syncUI();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
